// ============================================
// GLOBAL ACTION OVERLAY - Event-based (no React context needed)
// Compass Star icon spinning + progress percentage
// ============================================
import React, { useState, useEffect } from 'react';

// Global event bus for overlay control
const overlayBus = {
  _listeners: [],
  _state: { visible: false, message: '', progress: 0 },
  show(message = 'Procesando...', progress = 0) {
    this._state = { visible: true, message, progress };
    this._notify();
  },
  update(message, progress) {
    if (message !== undefined) this._state.message = message;
    if (progress !== undefined) this._state.progress = progress;
    this._notify();
  },
  hide() {
    this._state = { ...this._state, progress: 100, message: 'Completado' };
    this._notify();
    setTimeout(() => {
      this._state = { visible: false, message: '', progress: 0 };
      this._notify();
    }, 600);
  },
  subscribe(fn) { this._listeners.push(fn); return () => { this._listeners = this._listeners.filter(l => l !== fn); }; },
  _notify() { this._listeners.forEach(fn => fn({ ...this._state })); }
};

// Export simple functions (work anywhere, no hooks needed)
export const showOverlay = (msg, pct) => overlayBus.show(msg, pct);
export const updateOverlay = (msg, pct) => overlayBus.update(msg, pct);
export const hideOverlay = () => overlayBus.hide();

// Hook for convenience (optional)
export const useActionOverlay = () => ({ showOverlay, updateOverlay, hideOverlay });

// Compass Star SVG
const CompassStar = ({ size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <polygon points="50,2 56,38 50,32 44,38" fill="url(#cg1)" />
    <polygon points="98,50 62,56 68,50 62,44" fill="url(#cg2)" />
    <polygon points="50,98 44,62 50,68 56,62" fill="url(#cg1)" />
    <polygon points="2,50 38,44 32,50 38,56" fill="url(#cg2)" />
    <polygon points="85,15 60,42 62,38 58,40" fill="url(#cg3)" opacity="0.7" />
    <polygon points="85,85 58,60 62,62 60,58" fill="url(#cg3)" opacity="0.7" />
    <polygon points="15,85 40,58 38,62 42,60" fill="url(#cg3)" opacity="0.7" />
    <polygon points="15,15 42,40 38,38 40,42" fill="url(#cg3)" opacity="0.7" />
    <circle cx="50" cy="50" r="8" fill="url(#cg4)" />
    <circle cx="50" cy="50" r="4" fill="white" opacity="0.9" />
    <defs>
      <linearGradient id="cg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#10b981"/><stop offset="100%" stopColor="#059669"/></linearGradient>
      <linearGradient id="cg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#14b8a6"/><stop offset="100%" stopColor="#0d9488"/></linearGradient>
      <linearGradient id="cg3" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#6ee7b7"/><stop offset="100%" stopColor="#34d399"/></linearGradient>
      <linearGradient id="cg4" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#065f46"/><stop offset="100%" stopColor="#047857"/></linearGradient>
    </defs>
  </svg>
);

// The UI component - place once at app root
export const ActionOverlayUI = () => {
  const [state, setState] = useState({ visible: false, message: '', progress: 0 });

  useEffect(() => {
    return overlayBus.subscribe(setState);
  }, []);

  if (!state.visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)' }}
      data-testid="global-action-overlay"
    >
      <div className="flex flex-col items-center gap-5">
        <div className="relative">
          <div className="animate-spin" style={{ animationDuration: '3s' }}>
            <CompassStar size={80} />
          </div>
          <svg className="absolute -inset-5" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="55" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
            <circle
              cx="60" cy="60" r="55" fill="none"
              stroke="url(#pgGrad)" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${(state.progress / 100) * 345.58} 345.58`}
              transform="rotate(-90 60 60)"
              className="transition-all duration-700 ease-out"
            />
            <defs>
              <linearGradient id="pgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981"/><stop offset="100%" stopColor="#06b6d4"/>
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="text-3xl font-bold text-white tracking-wider" data-testid="overlay-progress">
          {Math.round(state.progress)}<span className="text-lg text-emerald-400">%</span>
        </div>

        <div className="text-center max-w-sm">
          <p className="text-white/90 text-sm font-medium" data-testid="overlay-message">{state.message}</p>
          <p className="text-white/40 text-xs mt-2">Por favor espere, no cierre esta ventana</p>
        </div>

        <div className="w-64 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${state.progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Provider wrapper (for compatibility)
export const ActionOverlayProvider = ({ children }) => (
  <>
    {children}
    <ActionOverlayUI />
  </>
);

export default ActionOverlayProvider;
